/* eslint-disable indent */
/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { FaEdit, FaTrash, FaSyncAlt, FaFileInvoiceDollar, FaMoneyBillWave } from "react-icons/fa";
import { CarteleriaForm } from "..";
import { useAuthContext } from "../../context/auth";
import { useCurrency, getUsers, isContratoVencido, getMesesExtendidos } from "../../utils";
import { getMesesPorFactura } from "../../utils/lineasFacturacion/getMesesPorFactura";
import { client } from "../../utils/supabase";
import CarteleriaCostosModal from "../CarteleriaCostosModal";
import FacturacionHardwareModal from "../FacturacionHardwareModal";
import KebabMenu from "../KebabMenu";
import styles from "./CarteleriaList.module.scss";

const CarteleriaList = ({ refreshKey, showFormModal, setShowFormModal }) => {
  const [negocios, setNegocios] = useState([]);
  const [clientes, setClientes] = useState({});
  const [edificios, setEdificios] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [usuarios, setUsuarios] = useState({});
  const [showPagosModal, setShowPagosModal] = useState(false);
  const [currentNegocioId, setCurrentNegocioId] = useState(null);
  const [montoTotalHardware, setMontoTotalHardware] = useState(0);
  const [pagosHardware, setPagosHardware] = useState([]);
  const [monedaHardware, setMonedaHardware] = useState("CLP");
  const [showOtrosModal, setShowOtrosModal] = useState(false);
  const [currentNegocioIdOtros, setCurrentNegocioIdOtros] = useState(null);
  const [otrosHardware, setOtrosHardware] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Modo "Renovar": id de la oportunidad origen que se está renovando (venta nueva).
  const [renovarOrigenId, setRenovarOrigenId] = useState(null);
  // Negocio cuyo split de facturación de hardware CAPEX se está declarando (modal por %).
  const [facturacionHwNegocio, setFacturacionHwNegocio] = useState(null);
  // Negocio cuya ventana "Costos" (Fase 2, costos_proyecto) está abierta — entidad 100% nueva y
  // aislada, no toca activos/plan_pagos_activos/Futuros/ER (ver docs/decisiones/2026-10-03-costos-
  // carteleria-fase2.md).
  const [costosNegocio, setCostosNegocio] = useState(null);
  // L6 tramo 5c (10.264): insumo de `getMesesPorFactura` — periodicidad por PUENTE/linea_id cuando
  // el folio resuelve a un documento real; por folio de texto cuando no (Perú/pre-2026, sin cambio).
  const [lineaIdPorClaveGestion, setLineaIdPorClaveGestion] = useState(new Map());
  const [facturaIdPorLineaGestion, setFacturaIdPorLineaGestion] = useState(new Map());
  const [foliosGestionConDocumento, setFoliosGestionConDocumento] = useState(new Set());

  const { pais, id: userId } = useAuthContext();
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : null;

  const { loadingRates, convert, formatAmount } = useCurrency();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [clientesResponse, usuarios, edificiosResponse, negociosResponse] = await Promise.all([
        client.from("clientes").select("id, nombre").eq("pais", normalizedPais).order("nombre", { ascending: true }),
        // getUsers pagina con .range() (evita el cap max-rows=2000); trae todos incl. deshabilitados.
        // El resultado alimenta un map id→nombre, así que el orden es irrelevante.
        getUsers({ includeDisabled: true }),
        client
          .from("edificios")
          .select("id, edificio")
          .eq("pais", normalizedPais)
          .order("edificio", { ascending: true }),
        client
          .from("negocios_carteleria")
          .select("*")
          .eq("pais", normalizedPais)
          .eq("eliminada", false)
          .order("id", { ascending: false }),
      ]);

      if (clientesResponse.error) throw new Error(`Clientes: ${clientesResponse.error.message}`);
      if (edificiosResponse.error) throw new Error(`Edificios: ${edificiosResponse.error.message}`);
      if (negociosResponse.error) throw new Error(`Negocios: ${negociosResponse.error.message}`);

      setClientes(clientesResponse.data.reduce((acc, c) => ({ ...acc, [c.id]: c.nombre }), {}));
      setEdificios(edificiosResponse.data.reduce((acc, e) => ({ ...acc, [e.id]: e.edificio }), {}));
      const negs = negociosResponse.data || [];
      setNegocios(negs);
      setUsuarios(usuarios.reduce((acc, u) => ({ ...acc, [u.id]: u.nombre }), {}));

      // L6 tramo 5c (10.264): insumo de getMesesPorFactura — solo lectura, batch por país.
      const foliosGestion = new Set();
      for (const n of negs) {
        const arr =
          typeof n.meses_facturacion === "string" ? JSON.parse(n.meses_facturacion || "[]") : n.meses_facturacion || [];
        for (const e of arr) if (e?.tipo === "gestion" && e.facturanumero) foliosGestion.add(String(e.facturanumero));
      }
      const negocioIds = negs.map((n) => String(n.id));
      const [lineasGestionRes, docsRes] = await Promise.all([
        negocioIds.length > 0
          ? client
              .from("lineas_facturacion")
              .select("id, negocio_id, ano, mes")
              .in("negocio_id", negocioIds)
              .eq("tipo", "gestion")
          : Promise.resolve({ data: [] }),
        foliosGestion.size > 0
          ? // Mismo criterio de "documento en el sistema" que `siiMap`/`tieneFactura` (10.1): requiere
            // `estado_vinculacion='vinculada'`, no solo que exista la fila (paridad con useVFDataLoading).
            client
              .from("facturas_sii_emitidas")
              .select("folio")
              .eq("pais", normalizedPais)
              .eq("estado_vinculacion", "vinculada")
              .in("folio", [...foliosGestion])
          : Promise.resolve({ data: [] }),
      ]);
      const lineaMap = new Map((lineasGestionRes.data || []).map((l) => [`${l.negocio_id}-${l.ano}-${l.mes}`, l.id]));
      const lineaIds = (lineasGestionRes.data || []).map((l) => l.id);
      let facturaPorLinea = new Map();
      if (lineaIds.length > 0) {
        const { data: puenteGestion } = await client
          .from("facturas_sii_vinculaciones")
          .select("linea_id, factura_sii_emitida_id")
          .in("linea_id", lineaIds);
        facturaPorLinea = new Map((puenteGestion || []).map((v) => [v.linea_id, v.factura_sii_emitida_id]));
      }
      setLineaIdPorClaveGestion(lineaMap);
      setFacturaIdPorLineaGestion(facturaPorLinea);
      setFoliosGestionConDocumento(new Set((docsRes.data || []).map((d) => String(d.folio))));
    } catch (err) {
      console.error("Error fetching data:", err.message);
      setError(err.message);
      toast.error("Error al cargar los datos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!normalizedPais) {
      setError("No se ha definido el país del usuario");
      setLoading(false);
      return;
    }
    fetchData();
  }, [normalizedPais, refreshKey]);

  const deleteNegocio = (id) => setDeleteId(id === deleteId ? null : id);

  const confirmDelete = async () => {
    if (!deleteId) return;
    const negocio = negocios.find((n) => n.id === deleteId);
    if (negocio.estado.toLowerCase() === "activo") {
      toast.error("No se puede eliminar un proyecto ACTIVO.");
      setDeleteId(null);
      return;
    }
    try {
      const { error } = await client.from("negocios_carteleria").update({ eliminada: true }).eq("id", deleteId);
      if (error) throw error;
      toast.success("Oportunidad marcada como eliminada");
      fetchData();
    } catch (error) {
      console.error("Error marking as deleted:", error);
      toast.error("Error al marcar como eliminada: " + error.message);
    } finally {
      setDeleteId(null);
    }
  };

  const formatMonthYear = (dateStr) => {
    if (!dateStr) return "-";
    // Parse manual para evitar timezone shift de "YYYY-MM-DD" → UTC midnight → local prev day
    const [year, month] = dateStr.split("-");
    return `${month}/${year}`;
  };

  const calculateFinDate = (fechaInicio, plazoMeses) => {
    if (!fechaInicio || !plazoMeses) return "-";

    const [yearStr, monthStr] = fechaInicio.split("-");
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10); // mes de inicio (1-12)

    const mesesContrato = parseInt(plazoMeses, 10);

    // Sumamos los meses del contrato al mes de inicio y restamos 1 para que termine el mes anterior
    let nuevoMes = month + mesesContrato - 1;

    // Si el plazo es 1 mes, termina el mismo mes de inicio
    if (nuevoMes < month) nuevoMes = month;

    year += Math.floor(nuevoMes / 12);
    month = nuevoMes % 12;
    if (month === 0) {
      month = 12;
      year -= 1;
    }

    const monthFormatted = String(month).padStart(2, "0");
    return `${monthFormatted}/${year}`;
  };
  const calcularSumaPagos = (pagos) => pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);

  // FIN del contrato + prórroga de hecho: definición única centralizada en
  // src/utils/carteleriaFacturacion.js (isContratoVencido / getMesesExtendidos), compartida
  // con la ventana de facturación de VentasFacturas (regla 10.1, no duplicar).

  const getStatusClass = (estado, vencido) => {
    if (vencido) return styles.statusVencido;
    switch (estado.toLowerCase()) {
      case "activo":
        return styles.statusActivo;
      case "100%":
        return styles.status100;
      case "90%":
        return styles.status90;
      case "75%":
        return styles.status75;
      case "50%":
        return styles.status50;
      case "0%":
        return styles.status0;
      case "cumplido":
        return styles.statusCumplido;
      default:
        return "";
    }
  };

  const parseFechaLocal = (fechaStr) => {
    if (!fechaStr) return null;
    const [year, month, day] = fechaStr.split("-");
    return new Date(year, month - 1, day);
  };

  const sortedNegocios = [...negocios].sort((a, b) => {
    // 'Cumplido' es terminal → al fondo (0), junto a los estados desconocidos.
    const stateOrder = { activo: 6, "100%": 5, "90%": 4, "75%": 3, "50%": 2, "0%": 1, cumplido: 0 };
    const estadoA = (a.estado || "").toString().toLowerCase();
    const estadoB = (b.estado || "").toString().toLowerCase();
    const stateA = stateOrder[estadoA] || 0;
    const stateB = stateOrder[estadoB] || 0;
    if (stateA !== stateB) return stateB - stateA;

    const dateA = new Date(a.fecha_inicio).getTime();
    const dateB = new Date(b.fecha_inicio).getTime();
    if (dateA !== dateB) return dateA - dateB;

    const nombreA =
      a.tipo_vertical === "TD"
        ? clientes[a.referencia_id] || `ID ${a.referencia_id}`
        : edificios[a.referencia_id] || `ID ${a.referencia_id}`;
    const nombreB =
      b.tipo_vertical === "TD"
        ? clientes[b.referencia_id] || `ID ${b.referencia_id}`
        : edificios[b.referencia_id] || `ID ${b.referencia_id}`;
    return nombreA.localeCompare(nombreB);
  });

  // Índice origen→renovaciones (por ID, regla 10.7): { "<origenId>": ["<idQueRenueva>", ...] }.
  // La lista viene toda cargada; oportunidad_origen_id e id son text. Puede haber más de una
  // renovación apuntando al mismo origen (no se asume una sola).
  const renovacionesPorOrigen = negocios.reduce((acc, n) => {
    const origen = n.oportunidad_origen_id != null ? String(n.oportunidad_origen_id) : "";
    if (origen) {
      if (!acc[origen]) acc[origen] = [];
      acc[origen].push(String(n.id));
    }
    return acc;
  }, {});

  if (error) return <div className="carteleria-list">{error}</div>;
  if (loading || loadingRates) return <div className="carteleria-list">Cargando oportunidades y tasas...</div>;

  const abrirModalPagos = (negocio) => {
    if (negocio.modalidad !== "CAPEX" || !negocio.incluye_hardware) return;
    setCurrentNegocioId(negocio.id);
    setMontoTotalHardware(parseFloat(negocio.monto_hardware) || 0);
    setMonedaHardware(negocio.moneda_hardware || "CLP");
    const pagosExistentes = negocio.detalle_pagos_hardware || [];
    // La fecha que identifica al tramo (clave natural, 10.1) es `facturafecha` si ya está facturado,
    // `fecha_factura` si no — misma prioridad que `derivarClaveHardware`/`nc_lineas_derivadas`. Antes
    // este mapeo solo miraba `fecha_factura`: para un tramo YA facturado (que no trae ese campo, solo
    // `facturafecha`) el picker se abría vacío y guardar sin cambios lo hacía parecer "sin fecha" ante
    // guardar_cuotas_hardware (10.177/10.227 — un campo existente que se pierde al re-guardar).
    setPagosHardware(
      pagosExistentes.map((p) => ({ monto: p.monto || 0, fecha_factura: p.facturafecha || p.fecha_factura || null }))
    );
    setShowPagosModal(true);
  };

  const abrirModalOtros = (negocio) => {
    if (negocio.modalidad !== "CAPEX" || !negocio.incluye_hardware) return;
    setCurrentNegocioIdOtros(negocio.id);
    const pagos = negocio.detalle_otros_hardware || [];
    setOtrosHardware(pagos.map((p) => ({ monto: p.monto || 0, fecha_factura: p.fecha_factura || null })));
    setShowOtrosModal(true);
  };

  const abrirFormModal = (id = null) => {
    setSelectedId(id);
    setRenovarOrigenId(null); // editar/crear normal: no es una renovación
    if (setShowFormModal) setShowFormModal(true);
  };

  // Abre el form en modo "Nueva renovación de #X": venta nueva (sin selectedId),
  // vinculada al contrato viejo por oportunidad_origen_id. NO toca el viejo.
  const abrirRenovarModal = (origenId) => {
    setSelectedId(null);
    setRenovarOrigenId(origenId);
    if (setShowFormModal) setShowFormModal(true);
  };

  const cerrarFormModal = () => {
    if (setShowFormModal) setShowFormModal(false);
    setSelectedId(null);
    setRenovarOrigenId(null);
  };

  const handleSuccess = () => {
    cerrarFormModal();
    fetchData(); // refresca la lista
  };

  const displayValue = (value, currency = "") => {
    if (value === null || value === undefined || value === 0 || value === "0") {
      return "-";
    }
    return currency ? formatAmount(value, currency) : value;
  };

  // Nombre a mostrar del proyecto (edificio/cliente referenciado, MAYÚSCULAS) — ÚNICA definición
  // (10.1), reusada por la celda "Cliente" de la tabla Y por el título del modal "Costos".
  const getNombreProyecto = (n) =>
    n.referencia_id && edificios[n.referencia_id]
      ? edificios[n.referencia_id].toUpperCase()
      : n.referencia_id && clientes[n.referencia_id]
        ? clientes[n.referencia_id].toUpperCase()
        : `ID ${n.referencia_id}`;

  // Etiqueta legible derivada del número de meses por factura. `getMesesPorFactura` (L6 tramo 5c,
  // 10.264) es un util puro en `lineasFacturacion/` — agrupa por puente/linea_id cuando el folio
  // resuelve a un documento real, por folio de texto cuando no (Perú/pre-2026, sin cambio).
  const getPeriodicidad = (n) => {
    // Venta puntual: OT en CAPEX sin cuotas ni plazo recurrente
    if (
      n.tipo_vertical === "OT" &&
      n.modalidad === "CAPEX" &&
      (!n.num_cuotas || Number(n.num_cuotas) <= 1) &&
      (!n.plazo_contrato_meses || Number(n.plazo_contrato_meses) <= 1)
    ) {
      return "Única vez";
    }
    const meses = getMesesPorFactura(n, {
      lineaIdPorClaveGestion,
      facturaIdPorLineaGestion,
      foliosGestionConDocumento,
    });
    const labels = { 1: "Mensual", 2: "Bimensual", 3: "Trimestral", 4: "Cuatrimestral", 6: "Semestral", 12: "Anual" };
    return labels[meses] || `Cada ${meses} meses`;
  };

  return (
    <div className={styles.carteleriaList}>
      {sortedNegocios.length === 0 ? (
        <div>No se encontraron oportunidades para {normalizedPais}</div>
      ) : (
        <>
          <table className={styles.carteleriaTable}>
            <colgroup>
              <col style={{ width: "3%" }} />
              <col style={{ width: "4%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "4%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "4%" }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan="4" style={{ backgroundColor: "#271313ff", color: "white" }}>
                  Negocio
                </th>
                <th colSpan="3" style={{ backgroundColor: "#403d00ff", color: "white" }}>
                  Contrato
                </th>
                <th colSpan="3" style={{ backgroundColor: "#003366", color: "white" }}>
                  Hardware
                </th>
                {/* Azul oscuro */}
                <th colSpan="2" style={{ backgroundColor: "#660000", color: "white" }}>
                  Gestión & Contenidos
                </th>
                {/* Rojo oscuro */}
                <th colSpan="4" style={{ backgroundColor: "#004d00", color: "white" }}>
                  Cuota
                </th>
                <th colSpan="3" style={{ backgroundColor: "#20002cff", color: "white" }}>
                  Total negocio
                </th>
                {/* Verde oscuro */}
                <th rowSpan="2">Edit</th>
              </tr>
              <tr>
                <th>Tipo</th>
                <th>Ejec.</th>
                <th>Cliente</th>
                <th>Status</th>
                <th>Pant.</th>
                <th>Inicio</th>
                <th>Fin</th>
                {/* Hardware */}
                <th>CAPEX</th>
                <th>OPEX</th>
                <th>Otros</th>
                {/* Gestión & Contenidos */}
                <th>Gestión</th>
                <th>Contenidos</th>
                {/* Cuota */}
                <th>Hardware</th>
                <th>Gestión</th>
                <th>Total</th>
                <th>Periodo</th>
                {/* Total negocio */}
                <th>Hardware</th>
                <th>Gestión</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedNegocios.map((n) => {
                const isActive = n.estado.toLowerCase() === "activo";
                const vencido = isActive && isContratoVencido(n.fecha_inicio, n.plazo_contrato_meses);
                const canEdit = vencido || !isActive;
                // Split de facturación de hardware: solo CAPEX con hardware y monto > 0.
                const puedeFacturarHw =
                  !!n.incluye_hardware &&
                  (n.modalidad || "").toUpperCase() === "CAPEX" &&
                  (parseFloat(n.monto_hardware) || 0) > 0;
                // Vínculos de renovación (derivado, sin escribir nada).
                const esRenovacionDe = n.oportunidad_origen_id ? String(n.oportunidad_origen_id) : null;
                const renovadaPor = renovacionesPorOrigen[String(n.id)] || []; // ids que la renuevan
                const fueRenovada = renovadaPor.length > 0;
                // Meses de prórroga de hecho (solo si vencido) y aviso de cierre pendiente.
                const mesesExtendidos = vencido ? getMesesExtendidos(n.fecha_inicio, n.plazo_contrato_meses) : 0;
                const avisoCerrar = vencido && fueRenovada;
                const monedaPrincipal = n.moneda_gestion || (normalizedPais === "Chile" ? "UF" : "PEN");
                const cantidadPantallas = parseInt(n.cantidad_pantallas, 10) || 1;
                const mesesContrato = parseInt(n.plazo_contrato_meses, 10) || 1;
                const gestionPorPantalla = parseFloat(n.fee_gestion_unitario || 0);
                const contenidosPorPantalla = n.incluye_contenidos ? parseFloat(n.fee_contenidos_unitario || 0) : 0;
                // Contenidos puede estar en moneda distinta a gestión (ej: gestión UF + contenidos CLP).
                // Convertir a moneda_gestion antes de sumar para no mezclar escalares incompatibles.
                const contenidosEnMonedaGestion =
                  contenidosPorPantalla > 0 && n.moneda_contenidos && n.moneda_contenidos !== n.moneda_gestion
                    ? convert(contenidosPorPantalla, n.moneda_contenidos, n.moneda_gestion)
                    : contenidosPorPantalla;
                const opexPorPantalla =
                  n.incluye_hardware && n.modalidad === "OPEX" ? parseFloat(n.cuota_hardware_opex || 0) : 0;

                const montoHardware = parseFloat(n.monto_hardware || 0);
                const otrosTotal = n.detalle_otros_hardware?.reduce((s, p) => s + Number(p.monto || 0), 0) || 0;
                const capexTotal = n.modalidad === "CAPEX" ? montoHardware + otrosTotal : 0;
                const opexTotalContrato =
                  n.modalidad === "OPEX" ? opexPorPantalla * cantidadPantallas * mesesContrato : 0;
                const totalHardwareContrato = capexTotal + opexTotalContrato;

                const gestionMensualBase = (gestionPorPantalla + contenidosEnMonedaGestion) * cantidadPantallas;
                const totalContratoGestionContenidos = gestionMensualBase * mesesContrato;

                // Cuota por factura: si la facturación es atípica (cuatrimestral, etc.),
                // multiplicar por la cantidad de meses que cubre cada factura.
                const mesesPorFactura = getMesesPorFactura(n, {
                  lineaIdPorClaveGestion,
                  facturaIdPorLineaGestion,
                  foliosGestionConDocumento,
                });
                const gestionMensual = gestionMensualBase * mesesPorFactura;
                const hardwareMensual = opexPorPantalla * cantidadPantallas * mesesPorFactura; // Solo en OPEX
                const totalMensual = hardwareMensual + gestionMensual;

                // Moneda local según país
                const monedaLocal = normalizedPais === "Chile" ? "CLP" : "PEN";

                // Total Hardware convertido a moneda local
                const totalHardwareEnLocal = n.moneda_hardware
                  ? convert(totalHardwareContrato, n.moneda_hardware, monedaLocal)
                  : totalHardwareContrato;

                // Total Gestión convertido a moneda local (moneda_gestion suele ser UF en Chile, PEN en Perú)
                const totalGestionEnLocal = n.moneda_gestion
                  ? convert(totalContratoGestionContenidos, n.moneda_gestion, monedaLocal)
                  : totalContratoGestionContenidos;

                // Total general = suma de ambos en moneda local
                const granTotalEnLocal = totalHardwareEnLocal + totalGestionEnLocal;

                return (
                  <tr key={n.id}>
                    <td>{n.tipo_vertical}</td>
                    <td>{usuarios[n.ejecutivo] || "-"}</td>
                    <td>
                      {(() => {
                        const nombreCompleto = getNombreProyecto(n);
                        return (
                          <>
                            <div
                              style={{
                                maxWidth: "180px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: n.nombre_proyecto ? "pointer" : "default",
                              }}
                              title={nombreCompleto}
                            >
                              {nombreCompleto}
                            </div>
                            {(esRenovacionDe || fueRenovada) && (
                              <div className={styles.renovBadges}>
                                {esRenovacionDe && (
                                  <span
                                    className={styles.renovBadge}
                                    title={`Es una renovación de la oportunidad #${esRenovacionDe}`}
                                  >
                                    Renovación de #{esRenovacionDe}
                                  </span>
                                )}
                                {renovadaPor.map((rid) => (
                                  <span
                                    key={rid}
                                    className={styles.renovadoBadge}
                                    title={`Renovada por la oportunidad #${rid}`}
                                  >
                                    Renovado por #{rid}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className={`uppercase ${isActive ? "active" : ""} ${getStatusClass(n.estado, vencido)}`}>
                      {vencido ? (
                        <>
                          {`VENCIDO${mesesExtendidos > 0 ? ` +${mesesExtendidos}` : ""}`}
                          {avisoCerrar && (
                            <span
                              className={styles.avisoCerrar}
                              title="Ya tiene una renovación cargada — recordá cerrar este contrato (Editar → Cumplido)"
                            >
                              ⚠️
                            </span>
                          )}
                        </>
                      ) : n.estado === "100%" ? (
                        "100%"
                      ) : n.estado === "Activo" ? (
                        "ACTIVO"
                      ) : (
                        n.estado.toUpperCase()
                      )}
                    </td>
                    <td>{displayValue(cantidadPantallas)}</td>{" "}
                    <td>{isActive ? formatMonthYear(n.fecha_inicio) : "-"}</td>
                    <td>{isActive ? calculateFinDate(n.fecha_inicio, n.plazo_contrato_meses) : "-"}</td>
                    {/* Hardware */}
                    <td>
                      {n.incluye_hardware && n.modalidad === "CAPEX" ? (
                        <span className="cursor-pointer" style={{ color: "#000" }} onClick={() => abrirModalPagos(n)}>
                          {formatAmount(n.monto_hardware, n.moneda_hardware)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {n.incluye_hardware && n.modalidad === "OPEX"
                        ? formatAmount(n.cuota_hardware_opex, n.moneda_hardware)
                        : "-"}
                    </td>
                    <td>
                      {n.incluye_hardware && n.modalidad === "CAPEX" ? (
                        <span className="cursor-pointer" style={{ color: "#000" }} onClick={() => abrirModalOtros(n)}>
                          {otrosTotal > 0 ? formatAmount(otrosTotal, n.moneda_hardware) : "—"}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    {/* Gestión & Contenidos */}
                    <td>{displayValue(gestionPorPantalla, n.moneda_gestion)}</td>{" "}
                    <td>
                      {n.incluye_contenidos
                        ? formatAmount(contenidosPorPantalla, n.moneda_contenidos || n.moneda_gestion)
                        : "-"}
                    </td>
                    {/* Totales */}
                    <td>{displayValue(hardwareMensual, monedaPrincipal)}</td>
                    <td>{displayValue(gestionMensual, monedaPrincipal)}</td>
                    <td style={{ fontWeight: "bold" }}>{displayValue(totalMensual, monedaPrincipal)}</td>
                    <td style={{ textAlign: "center" }}>{getPeriodicidad(n)}</td>
                    <td>{displayValue(totalHardwareEnLocal, monedaLocal)}</td>
                    <td>{displayValue(totalGestionEnLocal, monedaLocal)}</td>
                    <td>{displayValue(granTotalEnLocal, monedaLocal)}</td>
                    {/* Acciones (⋮) — patrón 10.136 */}
                    <td>
                      <KebabMenu
                        ariaLabel="Acciones de la oportunidad"
                        items={[
                          {
                            label: "Editar",
                            icon: <FaEdit />,
                            onClick: () => abrirFormModal(n.id),
                            disabled: !canEdit,
                            tooltip: canEdit ? "Editar" : "Negocio activo, no editable",
                          },
                          {
                            label: "Renovar",
                            icon: <FaSyncAlt />,
                            onClick: () => abrirRenovarModal(n.id),
                            tooltip: "Crear una renovación (venta nueva) a partir de esta oportunidad",
                          },
                          {
                            label: "Costos",
                            icon: <FaMoneyBillWave />,
                            onClick: () => setCostosNegocio(n),
                            // MISMO criterio que "Editar" (canEdit = vencido || !isActive, línea ~430
                            // de este archivo) — 10.279: se replica el permiso real existente, no se
                            // inventa uno nuevo para esta pantalla nueva.
                            disabled: !canEdit,
                            tooltip: canEdit ? "Costos previstos del proyecto" : "Negocio activo, no editable",
                          },
                          ...(puedeFacturarHw
                            ? [
                                {
                                  label: "Facturación hardware",
                                  icon: <FaFileInvoiceDollar />,
                                  onClick: () => setFacturacionHwNegocio(n),
                                  tooltip: "Declarar el split de facturación del hardware CAPEX en 2 tramos por %",
                                },
                              ]
                            : []),
                          {
                            label: "Eliminar",
                            icon: <FaTrash />,
                            danger: true,
                            onClick: () => deleteNegocio(n.id),
                            disabled: isActive,
                            tooltip: isActive ? "No se puede eliminar proyecto ACTIVO" : "Eliminar",
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Modales (pagos, otros, delete) - solo actualizo formato donde corresponde */}
      {showPagosModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowPagosModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Desglose de Cuotas Hardware</h5>
                <button type="button" className="btn-close" onClick={() => setShowPagosModal(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  <strong>Total hardware:</strong> {formatAmount(montoTotalHardware, monedaHardware)}
                </p>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Monto</th>
                      <th>Fecha Factura</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pagosHardware.map((pago, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={pago.monto}
                            onChange={(e) => {
                              const nuevos = [...pagosHardware];
                              nuevos[index].monto = e.target.value;
                              setPagosHardware(nuevos);
                            }}
                          />
                        </td>
                        <td>
                          <DatePicker
                            selected={pago.fecha_factura ? parseFechaLocal(pago.fecha_factura) : null}
                            onChange={(date) => {
                              const nuevos = [...pagosHardware];
                              nuevos[index].fecha_factura = date ? date.toISOString().split("T")[0] : null;
                              setPagosHardware(nuevos);
                            }}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            placeholderText="dd/mm/aaaa"
                          />
                        </td>
                        <td>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setPagosHardware(pagosHardware.filter((_, i) => i !== index))}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setPagosHardware([...pagosHardware, { monto: 0, fecha_factura: null }])}
                >
                  + Agregar cuota
                </button>
                <div className="mt-3">
                  <strong>Suma actual:</strong> {formatAmount(calcularSumaPagos(pagosHardware), monedaHardware)}
                  {calcularSumaPagos(pagosHardware) !== montoTotalHardware && (
                    <span className="text-danger ms-2">(debe coincidir con el total)</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPagosModal(false)}>
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const suma = calcularSumaPagos(pagosHardware);
                    if (suma !== montoTotalHardware) {
                      toast.error("La suma de las cuotas debe coincidir exactamente con el monto total de hardware.");
                      return;
                    }
                    const pagosLimpios = pagosHardware.filter((p) => p.monto > 0 && p.fecha_factura);

                    // L6 tramo 3: por RPC (guardar_cuotas_hardware). El candado L0 (tramo vinculado no se
                    // puede quitar/reordenar/cambiar de monto) corre en el servidor por clave natural, no
                    // en el cliente por índice; el mensaje que se muestra es el de la RPC (10.177).
                    const { error } = await client.rpc("guardar_cuotas_hardware", {
                      p_negocio_id: String(currentNegocioId),
                      p_cuotas: pagosLimpios,
                      p_usuario_id: parseInt(userId, 10) || null,
                    });
                    if (error) {
                      toast.error("Error al guardar: " + error.message);
                    } else {
                      toast.success("Cuotas guardadas correctamente");
                      setShowPagosModal(false);
                      fetchData();
                    }
                  }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ventana "Costos" (Fase 2, costos_proyecto) — entidad nueva y aislada, copia fiel de la
          ventana Inversión de Edificios. */}
      {costosNegocio && (
        <CarteleriaCostosModal
          negocio={costosNegocio}
          nombreProyecto={getNombreProyecto(costosNegocio)}
          userId={userId}
          onClose={() => setCostosNegocio(null)}
        />
      )}

      {/* Split de facturación de hardware CAPEX (2 tramos por %). Fuente única: detalle_pagos_hardware. */}
      {facturacionHwNegocio && (
        <FacturacionHardwareModal
          negocio={facturacionHwNegocio}
          onClose={() => setFacturacionHwNegocio(null)}
          onSaved={() => {
            setFacturacionHwNegocio(null);
            fetchData();
          }}
        />
      )}

      {/* Modal Otros y Delete se mantienen igual, solo formato con formatAmount donde corresponda */}

      {showOtrosModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowOtrosModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Otros costos (instalación, mano de obra, etc.)</h5>
                <button type="button" className="btn-close" onClick={() => setShowOtrosModal(false)} />
              </div>
              <div className="modal-body">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Monto</th>
                      <th>Fecha Factura</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {otrosHardware.map((pago, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={pago.monto}
                            onChange={(e) => {
                              const nuevos = [...otrosHardware];
                              nuevos[i].monto = e.target.value;
                              setOtrosHardware(nuevos);
                            }}
                          />
                        </td>
                        <td>
                          <DatePicker
                            selected={pago.fecha_factura ? parseFechaLocal(pago.fecha_factura) : null}
                            onChange={(date) => {
                              const nuevos = [...otrosHardware];
                              nuevos[i].fecha_factura = date ? date.toISOString().split("T")[0] : null;
                              setOtrosHardware(nuevos);
                            }}
                            dateFormat="dd/MM/yyyy"
                            className="form-control form-control-sm"
                            placeholderText="dd/mm/aaaa"
                          />
                        </td>
                        <td>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setOtrosHardware(otrosHardware.filter((_, idx) => idx !== i))}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setOtrosHardware([...otrosHardware, { monto: 0, fecha_factura: null }])}
                >
                  + Agregar otro
                </button>
                <div className="mt-3">
                  <strong>Total otros:</strong>{" "}
                  {formatAmount(
                    otrosHardware.reduce((s, p) => s + Number(p.monto || 0), 0),
                    monedaHardware
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowOtrosModal(false)}>
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const limpios = otrosHardware.filter((p) => p.monto > 0 && p.fecha_factura);
                    const { error } = await client
                      .from("negocios_carteleria")
                      .update({ detalle_otros_hardware: limpios })
                      .eq("id", currentNegocioIdOtros);
                    if (error) {
                      toast.error("Error al guardar: " + error.message);
                    } else {
                      toast.success("Otros costos guardados");
                      setShowOtrosModal(false);
                      fetchData();
                    }
                  }}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          onClick={() => setDeleteId(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow">
              <div className="modal-header border-0 pb-2">
                <h5 className="modal-title fw-bold">Confirmar eliminación</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteId(null)} aria-label="Cerrar" />
              </div>
              <div className="modal-body pt-2">
                <p className="mb-0 text-muted">
                  ¿Estás seguro de que deseas eliminar esta oportunidad?
                  <br />
                  <span className="text-danger fw-semibold">Esta acción no se puede deshacer.</span>
                </p>
              </div>
              <div className="modal-footer border-0 pt-2">
                <button type="button" className="btn btn-secondary" onClick={() => setDeleteId(null)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Sí, eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showFormModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={cerrarFormModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedId
                    ? `Editar oportunidad ID ${selectedId}`
                    : renovarOrigenId
                      ? `Nueva renovación de #${renovarOrigenId}`
                      : "Nueva oportunidad"}
                </h5>
                <button type="button" className="btn-close" onClick={cerrarFormModal} />
              </div>
              <div className="modal-body">
                <CarteleriaForm id={selectedId} renovarDeId={renovarOrigenId} onSuccess={handleSuccess} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarteleriaList;
